import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Button, TextField, Typography, Paper, CircularProgress, Alert,
    Stack, Table, TableHead, TableBody, TableRow, TableCell, IconButton
} from '@mui/material';
import * as api from './psychologicalPricesApi';
import type { PsychologicalPrice, PriceInterval } from './psychologicalPricesApi';
import { sx14, paperSx, FormRow, DetailHeader } from '../common/detailView';

interface Props {
    psychologicalPriceId: number | null;
}

const BLANK: PriceInterval = { minValue: 0, maxValue: 0, resultValue: 0 };

export function PsychologicalPriceView({ psychologicalPriceId }: Props) {
    const { t } = useTranslation();

    const [item, setItem] = useState<PsychologicalPrice | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [copy, setCopy] = useState<PsychologicalPrice | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [newInterval, setNewInterval] = useState<PriceInterval>({ ...BLANK });

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (psychologicalPriceId === null) {
                setItem({ id: 0, name: '', description: '', priceIntervals: [] });
                setIsNew(true);
                setIsEditing(true);
            } else {
                const data = await api.getPsychologicalPrice(psychologicalPriceId);
                data.priceIntervals = data.priceIntervals ?? [];
                setItem(data);
                setIsNew(false);
                setIsEditing(false);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [psychologicalPriceId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (error && !item) return <Alert severity="error">{error}</Alert>;
    if (!item) return null;

    const isNameValid = !!item.name && item.name.length > 0;
    const hasIntervals = item.priceIntervals.length > 0;
    // Legacy requires both name and at least one interval when creating;
    // existing records can save with any state.
    const canSave = !isNew || (isNameValid && hasIntervals);

    const startEdit = () => { setCopy(JSON.parse(JSON.stringify(item))); setIsEditing(true); };
    const cancelEdit = () => {
        if (copy) setItem(copy);
        setIsEditing(false);
        setNewInterval({ ...BLANK });
    };

    const save = async () => {
        setSaving(true); setError(null);
        try {
            const response = isNew
                ? await api.createPsychologicalPrice(item)
                : await api.updatePsychologicalPrice(item);
            response.priceIntervals = response.priceIntervals ?? [];
            setItem(response);
            setIsNew(false);
            setIsEditing(false);
            setNewInterval({ ...BLANK });
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const update = <K extends keyof PsychologicalPrice>(field: K, value: PsychologicalPrice[K]) => {
        setItem({ ...item, [field]: value });
    };

    const updateInterval = (index: number, patch: Partial<PriceInterval>) => {
        update('priceIntervals', item.priceIntervals.map((iv, i) => i === index ? { ...iv, ...patch } : iv));
    };
    const removeInterval = (index: number) => {
        update('priceIntervals', item.priceIntervals.filter((_, i) => i !== index));
    };
    const clearIntervals = () => {
        update('priceIntervals', []);
        setNewInterval({ ...BLANK });
    };

    const addInterval = () => {
        const { minValue, maxValue, resultValue } = newInterval;
        const min = Number(minValue);
        const max = Number(maxValue);
        if (Number.isNaN(min)) { setError(t('psychologicalPrice.minValueIsWrong')); return; }
        if (Number.isNaN(max)) { setError(t('psychologicalPrice.maxValueIsWrong')); return; }
        if (min > max) { setError(t('psychologicalPrice.minValueLargerThanMaxValue')); return; }

        const inserted: PriceInterval = { minValue: min, maxValue: max, resultValue: Number(resultValue) };
        update('priceIntervals', [...item.priceIntervals, inserted]);

        // Mirror the legacy "suggest next row" behavior: shift by the interval width.
        const diff = max - min;
        setNewInterval({
            minValue: max,
            maxValue: max + diff,
            resultValue: Number(resultValue) + diff,
        });
    };

    const sortedIntervals = isEditing
        ? item.priceIntervals.map((iv, i) => ({ iv, i }))
        : [...item.priceIntervals]
            .map((iv, i) => ({ iv, i }))
            .sort((a, b) => a.iv.minValue - b.iv.minValue);

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={item.name ?? ''}
                isEditing={isEditing}
                isNew={isNew}
                saving={saving}
                canSave={canSave}
                onEdit={startEdit}
                onSave={save}
                onCancel={cancelEdit}
            />

            <Box p={2}>
                <Stack spacing={2}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    <FormRow label={t('psychologicalPrice.name')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.name}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                value={item.name ?? ''}
                                onChange={e => update('name', e.target.value)}
                                error={!isNameValid} />
                        )}
                    </FormRow>

                    <FormRow label={t('psychologicalPrice.description')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.description}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                value={item.description ?? ''}
                                onChange={e => update('description', e.target.value)} />
                        )}
                    </FormRow>

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...sx14, fontWeight: 'bold', textAlign: 'right' }}>{t('common.min')}</TableCell>
                                <TableCell sx={{ ...sx14, fontWeight: 'bold', textAlign: 'right' }}>{t('common.max')}</TableCell>
                                <TableCell sx={{ ...sx14, fontWeight: 'bold', textAlign: 'right' }}>{t('common.value')}</TableCell>
                                {isEditing && (
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <Button size="small" variant="contained" color="warning"
                                            onClick={clearIntervals} disabled={!hasIntervals}>
                                            {t('common.clear')}
                                        </Button>
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isEditing && (
                                <TableRow>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <TextField size="small" type="number"
                                            value={newInterval.minValue}
                                            onChange={e => setNewInterval({ ...newInterval, minValue: e.target.value === '' ? 0 : Number(e.target.value) })}
                                            inputProps={{ style: { textAlign: 'right' } }} />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <TextField size="small" type="number"
                                            value={newInterval.maxValue}
                                            onChange={e => setNewInterval({ ...newInterval, maxValue: e.target.value === '' ? 0 : Number(e.target.value) })}
                                            inputProps={{ style: { textAlign: 'right' } }} />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <TextField size="small" type="number"
                                            value={newInterval.resultValue}
                                            onChange={e => setNewInterval({ ...newInterval, resultValue: e.target.value === '' ? 0 : Number(e.target.value) })}
                                            inputProps={{ style: { textAlign: 'right' } }} />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <Button size="small" variant="contained" color="secondary" onClick={addInterval}>
                                            {t('common.add')}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )}

                            {sortedIntervals.map(({ iv, i }) => (
                                <TableRow key={i}>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        {!isEditing ? (
                                            <Typography sx={sx14}>{iv.minValue.toFixed(2)}</Typography>
                                        ) : (
                                            <TextField size="small" type="number"
                                                value={iv.minValue}
                                                onChange={e => updateInterval(i, { minValue: e.target.value === '' ? 0 : Number(e.target.value) })}
                                                inputProps={{ style: { textAlign: 'right' } }} />
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        {!isEditing ? (
                                            <Typography sx={sx14}>{iv.maxValue.toFixed(2)}</Typography>
                                        ) : (
                                            <TextField size="small" type="number"
                                                value={iv.maxValue}
                                                onChange={e => updateInterval(i, { maxValue: e.target.value === '' ? 0 : Number(e.target.value) })}
                                                inputProps={{ style: { textAlign: 'right' } }} />
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        {!isEditing ? (
                                            <Typography sx={sx14}>{iv.resultValue.toFixed(2)}</Typography>
                                        ) : (
                                            <TextField size="small" type="number"
                                                value={iv.resultValue}
                                                onChange={e => updateInterval(i, { resultValue: e.target.value === '' ? 0 : Number(e.target.value) })}
                                                inputProps={{ style: { textAlign: 'right' } }} />
                                        )}
                                    </TableCell>
                                    {isEditing && (
                                        <TableCell sx={{ textAlign: 'right' }}>
                                            <IconButton size="small" color="error" onClick={() => removeInterval(i)}>×</IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Stack>
            </Box>

            {saving && <Box display="flex" justifyContent="center" p={1}><CircularProgress size={20} /></Box>}
        </Paper>
    );
}
